import React, { useEffect, useState } from "react";
import { PolicyFormValues, PolicyOption, PolicyValidations } from "../schemas/policy.types";
import { Form, useFormikContext } from "formik";
import { useUser } from "../../../custom-hooks/useUser";
import { Button, Col, Label, Row } from "reactstrap";
import { ComboCliente } from "../../../gen/ComboCliente";
import { BlkField } from "../../../coms/BlkField";
import { UploadPolicyFile } from "./UploadPolicyFile";
import { HealthInformationRow } from "../schemas/policy-excel.schema";
import { Alert } from "../../../interface/alert.interface";
import Select from "react-select";
import { SelectOption } from "../../../gen/estadisticas/schemas/kpi.types";

interface PolicyFormContentProps {
    alertRef: React.RefObject<Alert | undefined>;
    loading: boolean;
    isUploading: boolean;
    policyOptions: PolicyOption[];
    setHealthInformation: (data: HealthInformationRow[]) => void;
    handleDataByPolicy: (idClient: number, policy: string, idQuotations: number[], idInsurer?: number) => Promise<void>;
    handleProcessUpload: (values: PolicyFormValues) => void;
}

export const PolicyForm: React.FC<PolicyFormContentProps> = ({
    alertRef,
    loading,
    isUploading,
    policyOptions,
    setHealthInformation,
    handleDataByPolicy,
    handleProcessUpload,
}) => {
    const { isAdmin, isBroker, cdCliente: userClientCode } = useUser();
    const { values, setFieldValue } = useFormikContext<PolicyFormValues>();
    const [validities, setValidities] = useState<PolicyValidations[]>([]);

    useEffect(() => {
        if (!isAdmin && userClientCode) {
            setFieldValue("idClient", userClientCode);
        }
    }, [isAdmin, userClientCode, setFieldValue]);

    return (
        <Form className="container">
            <Row>
                {(isAdmin || isBroker) && (
                    <>
                        <Col lg={5}>
                            <Label>Clientes: </Label>
                            <ComboCliente
                                setFieldValue={setFieldValue}
                                name="cdCliente"
                                alert={alertRef}
                                values={values}
                                typePerson={2}
                            />
                        </Col>
                        <Col lg={1}>
                            <Button type="submit" className="btn__yellow mt-4" disabled={loading}>
                                Buscar
                            </Button>
                        </Col>
                    </>
                )}

                {policyOptions.length > 0 && (
                    <Col lg={6}>
                        <Label>Pólizas Encontradas: </Label>
                        <BlkField
                            component="select"
                            className="form-control w-100 inputTable"
                            name="polizaSelect"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                const policyValue = e.target.value;
                                void setFieldValue("polizaSelect", policyValue);
                                void setFieldValue("vigenciaSelect", []);
                                setHealthInformation([]);
                                setValidities([]);

                                if (!policyValue) {
                                    void setFieldValue("poliza", "");
                                    return;
                                }

                                const selected = policyOptions.find(
                                    item => `${item.policy}-${item.idInsuranceCompany}` === policyValue
                                );

                                if (selected) {
                                    void setFieldValue("poliza", selected.policy);
                                    void setFieldValue("cdAseguradora", selected.insuranceCompany);
                                    setValidities(selected.policyValidations || []);
                                }
                            }}
                        >
                            <option value="">-- Póliza --</option>
                            {policyOptions.map(item => (
                                <option
                                    key={`${item.policy}-${item.idInsuranceCompany}`}
                                    value={`${item.policy}-${item.idInsuranceCompany}`}
                                >
                                    {`${item.policy} - ${item.insuranceCompanyName}`}
                                </option>
                            ))}
                        </BlkField>
                    </Col>
                )}

                {validities.length > 0 && (
                    <Col lg={6} className="mt-3">
                        <Label>Vigencias: </Label>
                        <Select
                            isMulti
                            name="vigenciaSelect"
                            placeholder="-- Vigencias --"
                            classNamePrefix="react-select"
                            options={validities.map(v => ({
                                value: Number(v.idQuotation),
                                label: v.labelInformation,
                            }))}
                            value={(values.vigenciaSelect || [])
                                .map(val => {
                                    const found = validities.find(v => Number(v.idQuotation) === Number(val));
                                    return found
                                        ? { value: Number(found.idQuotation), label: found.labelInformation }
                                        : null;
                                })
                                .filter((o): o is SelectOption => o !== null)}
                            onChange={(selected: SelectOption[]) => {
                                const ids = selected ? selected.map(o => o.value) : [];
                                void setFieldValue("vigenciaSelect", ids);

                                const selectedDetails = validities.filter(v => ids.includes(Number(v.idQuotation)));
                                void setFieldValue("validityDetails", selectedDetails);

                                if (ids.length > 0) {
                                    const firstValidity = validities.find(v => Number(v.idQuotation) === ids[0]);
                                    if (firstValidity) {
                                        void setFieldValue("fcDesde", firstValidity.dateFrom);
                                        void setFieldValue("fcHasta", firstValidity.dateUntil);
                                    }

                                    const clientId =
                                        isAdmin || isBroker ? Number(values.cdCliente) : Number(userClientCode);
                                    void handleDataByPolicy(clientId, values.poliza, ids, Number(values.cdAseguradora));
                                } else {
                                    setHealthInformation([]);
                                }
                            }}
                        />
                    </Col>
                )}
            </Row>

            <Row className="d-flex flex-row-reverse">
                <UploadPolicyFile name="file" maxSizeMB={10} />
            </Row>

            <Row>
                <Col>
                    <Button
                        type="button"
                        color="primary"
                        className="mt-4"
                        disabled={isUploading || !values.poliza || !values.file || !values.vigenciaSelect?.length}
                        onClick={() => handleProcessUpload(values)}
                    >
                        Subir Excel
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};
